import {
  FaqAudience,
  faqAudienceLabel,
  FaqEntryInput,
  HelpTopic,
  helpTopicLabel,
} from '@kanos/domain';
import {
  Button,
  CheckboxGroupField,
  NumberField,
  SelectField,
  StatusBadge,
  TextField,
} from '@kanos/ui';
import {
  AddButton,
  Card,
  checkForm,
  fieldRule,
  formatDate,
  FormDialog,
  QueryContent,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DeleteEntryDialog, useEntryForm } from './entry-dialogs';
import { faqQuery, useDeleteFaq, useSaveFaq, type FaqEntry } from './queries';

const shape = FaqEntryInput.shape;
const topics = HelpTopic.options.map((id) => ({ id, label: helpTopicLabel[id] }));
const audiences = FaqAudience.options.map((id) => ({ id, label: faqAudienceLabel[id] }));

/** Writing a question, or changing one: `entry` is the one changed, `null` a new one. */
function FaqDialog({ entry, onClose }: { entry: FaqEntry | null; onClose: () => void }) {
  const save = useSaveFaq();
  const [topic, setTopic] = useState<HelpTopic>(entry?.topic ?? 'getting-started');
  const [audience, setAudience] = useState<FaqAudience>(entry?.audience ?? 'firm');
  const [question, setQuestion] = useState(entry?.question ?? '');
  const [answer, setAnswer] = useState(entry?.answer ?? '');
  const [position, setPosition] = useState<number | null>(entry?.position ?? 10);
  const [published, setPublished] = useState<'published'[]>(
    entry?.published === false ? [] : ['published'],
  );
  const form = useEntryForm(save, entry?.id ?? null, onClose);

  const submit = () => {
    const checked = checkForm(FaqEntryInput, {
      topic,
      audience,
      question,
      answer,
      position,
      published: published.length > 0,
    });
    form.submit(checked);
  };

  return (
    <FormDialog
      title={entry ? 'Frage ändern' : 'Frage hinzufügen'}
      description="Für die Hilfe der Apps. Eine leere Zeile in der Antwort beginnt einen neuen Absatz."
      submitLabel={entry ? 'Speichern' : 'Hinzufügen'}
      {...form.dialog}
      onSubmit={submit}
      draft={{ topic, audience, question, answer, position, published }}
      onClose={onClose}
    >
      <SelectField
        label="Thema"
        placeholder="Thema wählen"
        options={topics}
        value={topic}
        onChange={setTopic}
      />
      <SelectField
        label="Für"
        placeholder="App wählen"
        options={audiences}
        value={audience}
        onChange={setAudience}
      />
      <TextField
        label="Frage"
        placeholder="z. B. Wer kann den Vertrag zur Auftragsverarbeitung abschließen?"
        value={question}
        onChange={setQuestion}
        isRequired
        validate={fieldRule(shape.question, 'Bitte schreiben Sie die Frage (5 bis 300 Zeichen).')}
      />
      <TextField
        label="Antwort"
        placeholder="z. B. Admins der Kanzlei, unter Einstellungen › Datenschutz."
        multiline
        value={answer}
        onChange={setAnswer}
        isRequired
        validate={fieldRule(shape.answer, 'Bitte schreiben Sie die Antwort (10 bis 5000 Zeichen).')}
      />
      <NumberField
        label="Reihenfolge im Thema"
        placeholder="z. B. 10"
        description="Kleinere Zahlen stehen weiter oben."
        value={position}
        onChange={setPosition}
        minValue={0}
        maxValue={999}
        isRequired
      />
      <CheckboxGroupField
        label="Sichtbarkeit"
        options={[
          {
            id: 'published',
            label: 'Veröffentlicht',
            description: 'Sonst sieht nur das Kanos-Team die Frage.',
          },
        ]}
        value={published}
        onChange={setPublished}
      />
    </FormDialog>
  );
}

/**
 * The questions people ask (ADR-021 §6), kept by the Kanos team: by topic, published or not;
 * each change stands in the platform's audit log.
 */
export function FaqPage() {
  const faq = useQuery(faqQuery);
  const remove = useDeleteFaq();
  const [editing, setEditing] = useState<FaqEntry | 'new' | null>(null);
  const [deleting, setDeleting] = useState<FaqEntry | null>(null);

  return (
    <Card
      id="platform-faq"
      title="Häufige Fragen"
      description="Fragen und Antworten für die Hilfe der Apps, je Thema. Veröffentlichte Fragen erscheinen unter dem Hilfeartikel des Themas und in der Suche der Hilfe."
      action={<AddButton label="Frage hinzufügen" onPress={() => setEditing('new')} />}
    >
      <QueryContent query={faq}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Fragen.</p>
          ) : (
            HelpTopic.options
              .filter((topic) => list.some((e) => e.topic === topic))
              .map((topic) => (
                <section key={topic} aria-labelledby={`faq-${topic}`} className="grid gap-2">
                  <h3 id={`faq-${topic}`} className="font-semibold">
                    {helpTopicLabel[topic]}
                  </h3>
                  <ul className="divide-y divide-hairline border-y border-hairline">
                    {list
                      .filter((e) => e.topic === topic)
                      .map((entry) => (
                        <li
                          key={entry.id}
                          className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3"
                        >
                          <div className="grid min-w-0 flex-1 basis-64 gap-0.5">
                            <p className="font-medium">{entry.question}</p>
                            <p className="text-sm text-muted">
                              {faqAudienceLabel[entry.audience]} · Reihenfolge {entry.position} ·
                              geändert am {formatDate(entry.updatedAt)}
                            </p>
                          </div>
                          <StatusBadge tone={entry.published ? 'success' : 'neutral'}>
                            {entry.published ? 'Veröffentlicht' : 'Entwurf'}
                          </StatusBadge>
                          <Button variant="quiet" size="sm" onPress={() => setEditing(entry)}>
                            Ändern<span className="sr-only">: {entry.question}</span>
                          </Button>
                          <Button variant="quiet" size="sm" onPress={() => setDeleting(entry)}>
                            Löschen<span className="sr-only">: {entry.question}</span>
                          </Button>
                        </li>
                      ))}
                  </ul>
                </section>
              ))
          )
        }
      </QueryContent>
      {editing && (
        <FaqDialog entry={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {deleting && (
        <DeleteEntryDialog
          title="Frage löschen?"
          description={`„${deleting.question}“ verschwindet aus der Hilfe aller Apps.`}
          remove={remove}
          id={deleting.id}
          onClose={() => setDeleting(null)}
        />
      )}
    </Card>
  );
}
